/**
 * Minimal source document context preserved for template CSS selectors that
 * depend on html/body/page-wrapper classes.
 */

import { escapeHTML } from './html-escape.js';

const WRAPPER_TAGS = new Set(['div', 'main', 'section']);
const SECTION_PLACEMENT_BUCKETS = ['beforeWrapper', 'insideWrapper', 'afterWrapper'];

function cleanSpaceList(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

function cleanAttr(value) {
  return String(value || '').trim();
}

function cleanWrapperTag(value) {
  const tag = String(value || '')
    .trim()
    .toLowerCase();
  return WRAPPER_TAGS.has(tag) ? tag : '';
}

export function normalizeSourceTemplateDocument(value) {
  if (!value || typeof value !== 'object') return null;
  const out = {
    htmlClass: cleanSpaceList(value.htmlClass),
    bodyId: cleanAttr(value.bodyId),
    bodyClass: cleanSpaceList(value.bodyClass),
    wrapperTag: cleanWrapperTag(value.wrapperTag),
    wrapperId: cleanAttr(value.wrapperId),
    wrapperClass: cleanSpaceList(value.wrapperClass)
  };
  for (const key of Object.keys(out)) {
    if (!out[key]) delete out[key];
  }
  return Object.keys(out).length ? out : null;
}

export function sourceTemplateDocumentFromDocument(doc) {
  if (!doc) return null;
  const wrapper = doc.querySelector?.('#page-wrapper') || null;
  return normalizeSourceTemplateDocument({
    htmlClass: doc.documentElement?.getAttribute('class') || '',
    bodyId: doc.body?.getAttribute('id') || '',
    bodyClass: doc.body?.getAttribute('class') || '',
    wrapperTag: wrapper?.tagName?.toLowerCase() || '',
    wrapperId: wrapper?.getAttribute('id') || '',
    wrapperClass: wrapper?.getAttribute('class') || ''
  });
}

export function sourceTemplateSectionPlacementForElement(doc, element) {
  if (!doc?.body || !element) return 'insideWrapper';
  const wrapper = doc.querySelector?.('#page-wrapper') || null;
  if (!wrapper) return 'insideWrapper';
  if (element === wrapper || wrapper.contains(element)) return 'insideWrapper';

  const topLevelAncestor = (node) => {
    let current = node;
    while (current?.parentElement && current.parentElement !== doc.body) {
      current = current.parentElement;
    }
    return current?.parentElement === doc.body ? current : null;
  };

  const sectionTop = topLevelAncestor(element);
  const wrapperTop = topLevelAncestor(wrapper);
  if (!sectionTop || !wrapperTop || sectionTop === wrapperTop) return 'insideWrapper';

  const bodyChildren = Array.from(doc.body.children || []);
  const sectionIndex = bodyChildren.indexOf(sectionTop);
  const wrapperIndex = bodyChildren.indexOf(wrapperTop);
  if (sectionIndex < 0 || wrapperIndex < 0) return 'insideWrapper';
  if (sectionIndex < wrapperIndex) return 'beforeWrapper';
  if (sectionIndex > wrapperIndex) return 'afterWrapper';
  return 'insideWrapper';
}

export function normalizeSourceTemplateSectionPlacement(value) {
  if (!value || typeof value !== 'object') return null;
  const out = {};
  for (const bucket of SECTION_PLACEMENT_BUCKETS) {
    const items = Array.isArray(value[bucket]) ? value[bucket].map(cleanAttr).filter(Boolean) : [];
    if (items.length) out[bucket] = [...new Set(items)];
  }
  return Object.keys(out).length ? out : null;
}

export function splitSourceTemplateSections(items, placement, getId = (item) => item?.id) {
  const normalized = normalizeSourceTemplateSectionPlacement(placement);
  const out = { beforeWrapper: [], insideWrapper: [], afterWrapper: [] };
  if (!Array.isArray(items) || items.length === 0) return out;
  if (!normalized) {
    out.insideWrapper.push(...items);
    return out;
  }

  const before = new Set(normalized.beforeWrapper || []);
  const after = new Set(normalized.afterWrapper || []);
  for (const item of items) {
    const id = cleanAttr(getId(item));
    if (id && before.has(id)) out.beforeWrapper.push(item);
    else if (id && after.has(id)) out.afterWrapper.push(item);
    else out.insideWrapper.push(item);
  }
  return out;
}

export function sourceTemplateElementAttrs(sourceDocument, target) {
  const doc = normalizeSourceTemplateDocument(sourceDocument);
  if (!doc) return '';
  const attrs = [];
  if (target === 'html' && doc.htmlClass) attrs.push(['class', doc.htmlClass]);
  if (target === 'body') {
    if (doc.bodyId) attrs.push(['id', doc.bodyId]);
    if (doc.bodyClass) attrs.push(['class', doc.bodyClass]);
  }
  if (target === 'wrapper') {
    if (doc.wrapperId) attrs.push(['id', doc.wrapperId]);
    if (doc.wrapperClass) attrs.push(['class', doc.wrapperClass]);
  }
  return attrs.map(([name, value]) => ` ${name}="${escapeHTML(value)}"`).join('');
}

export function wrapSourceTemplateContent(sourceDocument, html) {
  const doc = normalizeSourceTemplateDocument(sourceDocument);
  if (!doc || (!doc.wrapperId && !doc.wrapperClass)) return html;
  const tag = doc.wrapperTag || 'div';
  return `<${tag}${sourceTemplateElementAttrs(doc, 'wrapper')}>\n${html}\n  </${tag}>`;
}
